import React from "react";
import OtpInput from "react-otp-input";
import {
  CalendarDays,
  Check,
  ChevronDown,
  Info,
  Send,
  ShieldCheck,
  X,
} from "lucide-react";

import { cn } from "../lib/utils";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";
import { SecuredFooter } from "./ui/secured-footer";
import {
  BackButton,
  BannerField,
  ContinueButton,
  FieldLabel,
  LargeGhostButton,
  LargeGradientButton,
  SecondaryField,
  WizardCard,
  YieldAssetBadge,
  ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND,
} from "./ui/zar8-yield-flow-ui";

function UnlockTermPicker({
  options,
  selectedPositionId,
  onSelect,
  onClose,
  className,
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-[#EBE9EF] bg-white py-2 shadow-[0_24px_80px_rgba(51,18,95,0.08)]",
        className
      )}
    >
      {options.map((option) => {
        const isSelected = option.id === selectedPositionId;

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => {
              onSelect(option.id);
              onClose();
            }}
            className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-[#FAF9FC]"
          >
            <div className="flex items-center gap-4">
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border",
                  isSelected
                    ? "border-[2px] border-[#FF315F]"
                    : "border-[#CECBD7] p-[2px]"
                )}
              >
                {isSelected ? (
                  <span className="h-5 w-5 rounded-full bg-[#FF315F]" />
                ) : null}
              </span>
              <span
                className={cn(
                  "text-base font-semibold leading-6",
                  isSelected ? "text-[#423E51]" : "text-[#6D6584]"
                )}
              >
                {option.label}
              </span>
            </div>
            <span className="rounded-full bg-[#EEF8F6] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.05em] text-[#17C3A5]">
              Active
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SummaryDetailRow({ label, value, strong = false, showInfo = false }) {
  return (
    <div className="flex min-h-[30px] items-center justify-between gap-4 px-4 py-2">
      <div className="flex items-center gap-2">
        <p
          className={cn(
            "text-sm leading-5 text-[#423E51]",
            strong && "font-semibold"
          )}
        >
          {label}
        </p>
        {showInfo ? <Info className="h-4 w-4 text-[#EB3366]" /> : null}
      </div>
      <p className="text-base font-semibold leading-6 text-[#423E51]">
        {value}
      </p>
    </div>
  );
}

export function UnlockConfirmationModal({ isConfirming, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(49,17,83,0.6)] px-4 backdrop-blur-[8px]">
      <div className="w-full max-w-[540px] rounded-[24px] border border-[#EBE9EF] bg-white px-10 pb-10 pt-6 shadow-[0_24px_80px_rgba(51,18,95,0.2)]">
        <div className="flex items-center justify-between gap-6">
          <h2 className="text-2xl font-semibold leading-8 text-[#57249C]">
            Unlock your ZAR8
          </h2>
          <button
            type="button"
            aria-label="Close confirmation"
            onClick={onClose}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-8 space-y-8">
          <p className="text-base leading-6 text-[#6D6584]">
            Unlocking will move part of your locked ZAR8 back to your available
            balance and stop it from earning yield.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="flex h-12 items-center justify-center rounded-full border border-[#EBE9EF] px-6 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
            >
              No, go back
            </button>
            <button
              type="button"
              disabled={isConfirming}
              onClick={onConfirm}
              className={cn(
                "flex h-12 items-center justify-center rounded-full px-6 text-sm font-semibold leading-5 text-white transition-opacity",
                isConfirming && "cursor-not-allowed opacity-40"
              )}
              style={{ background: ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND }}
            >
              {isConfirming ? "Continuing..." : "Continue to 2FA"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function UnlockAmountStep({
  amountError,
  amountInput,
  lockedBalance,
  modeSwitch,
  onAmountInputChange,
  onBack,
  onCancel,
  onContinue,
  onMaxAmount,
  onPositionSelect,
  onToggleTermPicker,
  selectedUnlockPosition,
  topAsset,
  unlockablePositions,
  isTermPickerOpen,
}) {
  return (
    <WizardCard
      title="Unlock ZAR8"
      onCancel={onCancel}
      footer={
        <SecuredFooter className="mt-0 w-full border-[#EBE9EF] pb-8 pt-6" />
      }
    >
      {modeSwitch}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <FieldLabel>Pay with</FieldLabel>
          <SecondaryField>
            <div className="flex items-center gap-2">
              <YieldAssetBadge assetIcon={topAsset} />
              <p className="text-base font-semibold leading-6 text-[#423E51]">
                ZAR8
              </p>
            </div>
          </SecondaryField>
        </div>

        <div className="space-y-3">
          <FieldLabel>Locked balance</FieldLabel>
          <BannerField>
            <p className="text-[20px] font-semibold leading-6 text-[#6D6584]">
              {formatFiatWithTwoDecimals(lockedBalance)}
            </p>
          </BannerField>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <FieldLabel>Yield term</FieldLabel>
        <div className="relative">
          <SecondaryField className="justify-between">
            <button
              type="button"
              aria-label="Open unlock term selector"
              onClick={onToggleTermPicker}
              className="flex w-full items-center justify-between gap-4 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F6F3FA] text-[#6D6584]">
                  <CalendarDays className="h-5 w-5" />
                </span>
                <p
                  className={cn(
                    "text-base font-semibold leading-6",
                    selectedUnlockPosition ? "text-[#423E51]" : "text-[#6D6584]"
                  )}
                >
                  {selectedUnlockPosition?.label || "Select term"}
                </p>
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-[#6D6584] transition-transform",
                  isTermPickerOpen && "rotate-180"
                )}
              />
            </button>
          </SecondaryField>

          {isTermPickerOpen ? (
            <UnlockTermPicker
              className="absolute left-0 right-0 top-[calc(100%+12px)] z-20"
              options={unlockablePositions}
              selectedPositionId={selectedUnlockPosition?.id}
              onSelect={onPositionSelect}
              onClose={onToggleTermPicker}
            />
          ) : null}
        </div>
        {unlockablePositions.length === 0 ? (
          <p className="text-sm font-medium leading-5 text-[#DA2F58]">
            You don’t have any active yield terms available to unlock yet.
          </p>
        ) : null}
      </div>

      <div className="mt-4 space-y-3">
        <FieldLabel>Enter amount to unlock</FieldLabel>
        <SecondaryField className="flex-wrap gap-4 py-5">
          <div className="min-w-[170px] flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[24px] font-semibold leading-8 text-[#33125F]">
                R
              </span>
              <input
                value={amountInput}
                onChange={(event) => onAmountInputChange(event.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                className="min-w-0 flex-1 bg-transparent text-[24px] font-semibold leading-8 text-[#33125F] outline-none placeholder:text-[#9D96AF]"
              />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onMaxAmount}
              className="flex h-10 items-center justify-center rounded-full border border-[#EBE9EF] px-4 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
            >
              Max
            </button>
          </div>
        </SecondaryField>
        {amountError ? (
          <p className="text-sm font-medium leading-5 text-[#DA2F58]">
            {amountError}
          </p>
        ) : null}
      </div>

      <div className="mt-7 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
        <BackButton onClick={onBack} ariaLabel="Back to dashboard" />
        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      </div>
    </WizardCard>
  );
}

export function UnlockSummaryStep({
  amountValue,
  hasAcceptedTerms,
  isQuoteLoading,
  onBack,
  onCancel,
  onContinue,
  onToggleAcceptedTerms,
  summaryError,
  unlockSummary,
}) {
  return (
    <WizardCard
      title="Review unlock details"
      onCancel={onCancel}
      onHeaderBack={onBack}
      footer={
        <SecuredFooter className="mt-0 w-full border-[#EBE9EF] pb-8 pt-6" />
      }
    >
      <div className="mt-10 space-y-8">
        <BannerField>
          <p className="w-full text-center text-[24px] font-semibold leading-8 text-[#423E51]">
            {formatFiatWithTwoDecimals(amountValue)}
          </p>
        </BannerField>

        <div className="space-y-3">
          <FieldLabel>Transaction details</FieldLabel>
          <div className="overflow-hidden rounded-2xl border border-[#EBE9EF] py-4">
            <SummaryDetailRow
              label="Unlocking"
              value={formatFiatWithTwoDecimals(amountValue)}
            />
            <div className="mx-4 border-t border-[#EBE9EF]" />
            <SummaryDetailRow
              label="Penalty Fees"
              value={formatFiatWithTwoDecimals(unlockSummary.penaltyAmount)}
              showInfo
            />
            <div className="mx-4 border-t border-[#EBE9EF]" />
            <SummaryDetailRow
              label="Net amount received"
              value={formatFiatWithTwoDecimals(unlockSummary.netAmount)}
            />
            <div className="mx-4 border-t border-[#EBE9EF]" />
            <SummaryDetailRow
              label="Remaining Locked"
              value={formatFiatWithTwoDecimals(unlockSummary.remainingLocked)}
              strong
            />
          </div>
        </div>

        <button
          type="button"
          onClick={onToggleAcceptedTerms}
          className="mx-auto flex w-full max-w-[347px] items-center gap-4 py-4 text-left"
        >
          <span
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-[4px] border-2 transition-colors",
              hasAcceptedTerms
                ? "border-[#EB3366] bg-[#EB3366] text-white"
                : "border-[#9D96AF] bg-white text-transparent"
            )}
          >
            <Check className="h-5 w-5" />
          </span>
          <span className="flex-1 text-base leading-6 text-[#6D6584]">
            I accept the{" "}
            <span className="font-semibold text-[#33125F] underline">
              Terms and Conditions
            </span>
          </span>
        </button>

        <p className="mx-auto max-w-[373px] text-center text-sm leading-5 text-[#6D6584]">
          Yield is not guaranteed and subject to market volatility.
        </p>

        {summaryError ? (
          <p className="text-center text-sm font-medium leading-5 text-[#DA2F58]">
            {summaryError}
          </p>
        ) : null}
      </div>

      <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
        <BackButton onClick={onBack} />
        <ContinueButton onClick={onContinue} disabled={isQuoteLoading}>
          {isQuoteLoading ? "Preparing..." : "Continue"}
        </ContinueButton>
      </div>
    </WizardCard>
  );
}

export function UnlockTwoFactorStep({
  isTwoFactorReady,
  isVerifyingOtp,
  onBack,
  onCancel,
  onEnable2FA,
  onOtpCodeChange,
  onVerify,
  otpCode,
  otpError,
  summaryError,
}) {
  const hasOtpError = Boolean(otpError);

  return (
    <WizardCard
      title="Unlock your ZAR8"
      onCancel={onCancel}
      onHeaderBack={onBack}
      footer={
        <SecuredFooter className="mt-0 w-full border-[#EBE9EF] pb-8 pt-6" />
      }
    >
      {isTwoFactorReady ? (
        <div className="mt-10">
          <div className="mx-auto w-full max-w-[603px]">
            <div className="flex flex-col gap-6">
              <p className="text-[32px] font-semibold leading-10 text-[#57249C]">
                Verify your unlock
              </p>
              <p className="max-w-[560px] text-[20px] leading-8 text-[#423E51]">
                Enter the 6-digit verification code generated by your
                authentication app:
              </p>
            </div>

            <div className="mt-12 flex justify-center">
              <div className="relative">
                <span className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-[20px] text-[#EBE9EF]">
                  -
                </span>
                <OtpInput
                  value={otpCode}
                  onChange={onOtpCodeChange}
                  numInputs={6}
                  shouldAutoFocus
                  containerStyle={{
                    gap: "12px",
                    alignItems: "center",
                  }}
                  renderInput={(props, index) => (
                    <input
                      {...props}
                      aria-label={`2FA digit ${index + 1}`}
                      style={{
                        ...(props.style || {}),
                        width: "56px",
                        minWidth: "56px",
                        maxWidth: "56px",
                        height: "56px",
                      }}
                      className={cn(
                        "h-14 w-14 rounded-2xl border bg-white text-center text-[36px] font-normal leading-[44px] outline-none transition-colors",
                        hasOtpError
                          ? "border-[#DA2F58] text-[#DA2F58]"
                          : index === 0
                            ? "border-2 border-[#57249C] text-[#6D6584]"
                            : "border-[#CECBD7] text-[#6D6584] focus:border-[#57249C]",
                        index === 2 && "mr-8",
                        index === 3 && "ml-8"
                      )}
                    />
                  )}
                />
              </div>
            </div>

            {otpError ? (
              <p className="mt-5 text-center text-sm font-medium leading-5 text-[#DA2F58]">
                {otpError}
              </p>
            ) : null}

            {summaryError ? (
              <p className="mt-5 text-center text-sm font-medium leading-5 text-[#DA2F58]">
                {summaryError}
              </p>
            ) : null}

            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              <LargeGhostButton onClick={onCancel}>Cancel</LargeGhostButton>
              <LargeGradientButton onClick={onVerify} disabled={isVerifyingOtp}>
                {isVerifyingOtp ? "Verifying..." : "Verify & Continue"}
              </LargeGradientButton>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-10 pb-2 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#F8F4FD] text-[#57249C]">
            <ShieldCheck className="h-9 w-9" />
          </div>
          <p className="mt-8 text-2xl font-semibold leading-8 text-[#33125F]">
            Enable 2FA to continue
          </p>
          <p className="mx-auto mt-4 max-w-[452px] text-base leading-6 text-[#6D6584]">
            Unlocking ZAR8 requires 2-factor authentication before funds can be
            released. Enable 2FA and return to this step to complete the
            transaction.
          </p>

          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <BackButton onClick={onBack} />
            <ContinueButton onClick={onEnable2FA}>Enable 2FA</ContinueButton>
          </div>
        </div>
      )}
    </WizardCard>
  );
}

export function UnlockProcessingStep() {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white">
      <div className="relative flex min-h-[642px] items-center justify-center">
        <div className="absolute inset-0">
          <div className="absolute left-1/2 top-1/2 h-[410px] w-[410px] -translate-x-[55%] -translate-y-[58%] rounded-full bg-[#7D2DB7]/35 blur-[90px]" />
          <div className="absolute left-1/2 top-1/2 h-[360px] w-[360px] -translate-x-[15%] -translate-y-[58%] rounded-full bg-[#EB3366]/35 blur-[100px]" />
          <div className="absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-[5%] -translate-y-[-5%] rounded-full bg-[#00D9FF]/28 blur-[110px]" />
        </div>
        <div className="relative flex flex-col items-center">
          <span className="text-[48px] font-semibold leading-none tracking-[-0.04em] text-white">
            80eight
          </span>
        </div>
      </div>
    </div>
  );
}

function SuccessIllustration() {
  return (
    <div className="relative h-[136px] w-[194px]">
      <span className="absolute left-[19px] top-[16px] h-[48px] w-[48px] rounded-full bg-[#F2E9FB]" />
      <span className="absolute left-[154px] top-[7px] h-[10px] w-[10px] rounded-full bg-[#00E2FF]" />
      <span className="absolute left-[140px] top-[76px] text-[34px] leading-none text-[#D342A9]">
        ✦
      </span>
      <div className="absolute left-[48px] top-[18px] rotate-[-12deg] text-[#E53672]">
        <Send className="h-[92px] w-[92px] stroke-[1.5]" />
      </div>
    </div>
  );
}

export function UnlockSuccessStep({ onDone }) {
  return (
    <div className="overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white pt-5">
      <div className="mx-auto flex w-full max-w-[709px] flex-col items-center gap-16 px-6 pb-[104px] pt-[74px] text-center">
        <div className="flex flex-col items-center gap-12">
          <SuccessIllustration />
          <div className="flex max-w-[358px] flex-col gap-6">
            <p className="text-[32px] font-semibold leading-10 text-[#57249C]">
              You unlocked successfully.
            </p>
            <p className="text-[20px] leading-8 text-[#423E51]">
              Your unlocked funds are now available in your ZAR8 balance.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onDone}
          className="h-12 w-full max-w-[302px] rounded-full text-sm font-semibold leading-5 text-white"
          style={{ background: ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
